import { useState } from 'react';
import { Income, EditableIncome } from '../hooks/useIncomes';
import { PanelBase } from './PanelBase';
import { TableViewBase } from './TableViewBase';
import { formatCurrency, Frequency } from '../utils';
import { showError, showSuccess } from 'src/notifications/notifications';
import { IncomeModal } from './IncomeModal';
import { useDisclosure } from '@mantine/hooks';
import { UnderlineTooltip } from 'src/UI/common';

const incomeTypeOptions: { value: string; label: string }[] = [
  { value: 'Income', label: 'Income' },
  { value: 'Benefit', label: 'Benefit' },
  { value: 'Employment', label: 'Employment' },
  { value: 'ShareOrDividend', label: 'Share or Dividend' },
  { value: 'Rental', label: 'Rental' },
  { value: 'Pension', label: 'Pension' },
];

type IncomePanelProps = {
  items: Income[];
  onSave: (data: EditableIncome[]) => Promise<void>;
  onDelete: (ids: string[]) => Promise<void>;
  frequency: Frequency;
};

export function IncomePanel({ items = [], onSave, onDelete, frequency }: IncomePanelProps) {
  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false);
  const [selectedIncome, setSelectedIncome] = useState<EditableIncome | undefined>();

  const handleSave = async (income: EditableIncome) => {
    try {
      await onSave([income]);
      showSuccess('Income saved successfully');
      closeModal();
      setSelectedIncome(undefined);
    } catch {
      showError('Failed to save income');
    }
  };

  const handleEdit = (income: Income) => {
    setSelectedIncome(income);
    openModal();
  };

  const handleAdd = () => {
    setSelectedIncome(undefined);
    openModal();
  };

  const handleDelete = (income: Income) => {
    if (income.id) {
      onDelete([income.id]);
    }
  };

  const renderMappedValue = (map: { value: string; label: string }[], value: string | number) => {
    const found = map.find(m => m.value === value);
    return found ? found.label : '';
  };

  const renderTableView = () => (
    <TableViewBase
      items={items}
      columns={[
        {
          key: 'incomeType',
          header: 'Type',
          sortable: true,
          renderValue: value => renderMappedValue(incomeTypeOptions, value),
        },
        { key: 'description', header: 'Description', sortable: true },
        { key: 'frequency', header: 'Frequency', sortable: true },
        {
          key: frequency === 'monthly' ? 'amountPreTaxMonthly' : 'amountPreTaxAnnually',
          header: `Before tax (${frequency})`,
          sortable: true,
          sortKey: frequency === 'monthly' ? 'amountPreTaxMonthly' : 'amountPreTaxAnnually',
          renderValue: (value, item) => {
            const displayValue =
              frequency === 'monthly' ? item.amountPreTaxMonthly : item.amountPreTaxAnnually;
            const tooltipValue =
              frequency === 'monthly' ? item.amountPreTaxAnnually : item.amountPreTaxMonthly;
            return (
              <UnderlineTooltip
                label={`${
                  frequency === 'monthly' ? 'Annually' : 'Monthly'
                }: ${formatCurrency(tooltipValue)}`}
              >
                {formatCurrency(displayValue)}
              </UnderlineTooltip>
            );
          },
          align: 'right',
        },
        {
          key: frequency === 'monthly' ? 'amountPostTaxMonthly' : 'amountPostTaxAnnually',
          header: `After tax (${frequency})`,
          sortable: true,
          sortKey: frequency === 'monthly' ? 'amountPostTaxMonthly' : 'amountPostTaxAnnually',
          renderValue: (value, item) => {
            const displayValue =
              frequency === 'monthly' ? item.amountPostTaxMonthly : item.amountPostTaxAnnually;
            const tooltipValue =
              frequency === 'monthly' ? item.amountPostTaxAnnually : item.amountPostTaxMonthly;
            return (
              <UnderlineTooltip
                label={`${
                  frequency === 'monthly' ? 'Annually' : 'Monthly'
                }: ${formatCurrency(tooltipValue)}`}
              >
                {formatCurrency(displayValue)}
              </UnderlineTooltip>
            );
          },
          align: 'right',
        },
      ]}
      onEdit={handleEdit}
      onDelete={handleDelete}
    />
  );

  return (
    <>
      <PanelBase<EditableIncome>
        items={items}
        addButtonLabel="Add a new income"
        renderTableView={renderTableView}
        onAdd={handleAdd}
      />
      <IncomeModal
        opened={modalOpened}
        onClose={closeModal}
        onSubmit={handleSave}
        initialValues={selectedIncome}
      />
    </>
  );
}
